/**
 * ROWIE — You may also like / Recently viewed
 *
 * Tab switching plus a lazily-built recently viewed track. Tiles are
 * fetched through the Section Rendering API rather than rebuilt in JS,
 * so swatches, prices and badges keep working.
 */

(function () {
  const ROOT = '[data-rowie-ptabs]';

  /* ---------------------------------------------------------------
     Carousel — shared by both panels
     --------------------------------------------------------------- */

  function initCarousel(wrap) {
    if (!wrap || wrap.dataset.carouselReady) return;
    wrap.dataset.carouselReady = '1';

    const track = wrap.querySelector('[data-ptabs-track]');
    const fill = wrap.querySelector('[data-ptabs-fill]');
    const prev = wrap.querySelector('[data-ptabs-arrow="prev"]');
    const next = wrap.querySelector('[data-ptabs-arrow="next"]');
    if (!track) return;

    const EDGE = 2;

    function update() {
      const max = track.scrollWidth - track.clientWidth;

      if (max <= EDGE) {
        if (fill) fill.style.width = '100%';
        if (prev) prev.disabled = true;
        if (next) next.disabled = true;
        return;
      }

      const ratio = track.scrollLeft / max;
      const view = Math.max((track.clientWidth / track.scrollWidth) * 100, 12);

      if (fill) {
        fill.style.width = view + '%';
        fill.style.left = ratio * (100 - view) + '%';
      }
      if (prev) prev.disabled = track.scrollLeft <= EDGE;
      if (next) next.disabled = track.scrollLeft >= max - EDGE;
    }

    function step(dir) {
      const amount = track.clientWidth * 0.8;
      track.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', () => step('prev'));
    if (next) next.addEventListener('click', () => step('next'));
    track.addEventListener('scroll', update, { passive: true });

    let frame;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    });

    update();
    window.addEventListener('load', update);
    wrap._ptabsUpdate = update;
  }

  /* ---------------------------------------------------------------
     Recently viewed
     --------------------------------------------------------------- */

  async function fetchTile(handle) {
    try {
      const res = await fetch(
        '/products/' + encodeURIComponent(handle) + '?section_id=rowie-tile-single'
      );
      if (!res.ok) return null;
      const html = await res.text();
      return html && html.trim() ? html : null;
    } catch (e) {
      return null;
    }
  }

  async function buildRecent(root) {
    const panel = root.querySelector('[data-ppanel="recent"]');
    const tab = root.querySelector('[data-ptab="recent"]');
    if (!panel || !tab) return;

    const store = window.RowieRecentlyViewed;
    if (!store) return;

    const current = root.dataset.productHandle;
    const limit = parseInt(root.dataset.limit, 10) || 10;

    // Standing on a PDP you have by definition just viewed it.
    const handles = store
      .get()
      .filter((h) => h && h !== current)
      .slice(0, limit);

    if (handles.length === 0) return;

    // Parallel, but keep view order: a failed fetch drops out rather
    // than shifting everything after it.
    const results = await Promise.all(handles.map(fetchTile));
    const tiles = results.filter(Boolean);
    if (tiles.length === 0) return;

    panel.innerHTML =
      '<div class="rowie-ptabs__carousel" data-ptabs-carousel data-single-image-tiles>' +
      '<ul class="rowie-ptabs__track" data-ptabs-track>' +
      tiles.join('') +
      '</ul>' +
      '<div class="rowie-ptabs__navrow">' +
      '<div class="rowie-ptabs__rail"><div class="rowie-ptabs__fill" data-ptabs-fill></div></div>' +
      '<div class="rowie-ptabs__arrows">' +
      '<button type="button" class="rowie-ptabs__arrow" data-ptabs-arrow="prev" aria-label="Previous products"></button>' +
      '<button type="button" class="rowie-ptabs__arrow" data-ptabs-arrow="next" aria-label="Next products"></button>' +
      '</div></div></div>';

    // Borrow the arrow glyphs from the first panel so both tabs match
    // without duplicating the icon markup.
    const source = root.querySelector('[data-ppanel="related"] [data-ptabs-arrow="prev"]');
    const sourceNext = root.querySelector('[data-ppanel="related"] [data-ptabs-arrow="next"]');
    const destPrev = panel.querySelector('[data-ptabs-arrow="prev"]');
    const destNext = panel.querySelector('[data-ptabs-arrow="next"]');
    if (source && destPrev) destPrev.innerHTML = source.innerHTML;
    if (sourceNext && destNext) destNext.innerHTML = sourceNext.innerHTML;

    tab.hidden = false;
    initCarousel(panel.querySelector('[data-ptabs-carousel]'));
  }

  /* ---------------------------------------------------------------
     Tabs
     --------------------------------------------------------------- */

  function initTabs(root) {
    const tabs = [...root.querySelectorAll('[data-ptab]')];
    const panels = [...root.querySelectorAll('[data-ppanel]')];

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const name = tab.dataset.ptab;

        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        panels.forEach((p) => {
          const active = p.dataset.ppanel === name;
          p.hidden = !active;
          p.classList.toggle('is-active', active);

          // A track measured while hidden reports zero width.
          if (active) {
            const wrap = p.querySelector('[data-ptabs-carousel]');
            if (wrap && wrap._ptabsUpdate) wrap._ptabsUpdate();
          }
        });
      });
    });
  }

  function init(root) {
    initTabs(root);
    root.querySelectorAll('[data-ptabs-carousel]').forEach(initCarousel);
    buildRecent(root);
  }

  function initAll() {
    document.querySelectorAll(ROOT).forEach((root) => {
      if (root.dataset.initialised) return;
      root.dataset.initialised = 'true';
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(ROOT).forEach((r) => r.removeAttribute('data-initialised'));
    initAll();
  });
})();